import { supabase } from '../lib/supabase';

/**
 * MODELO ÚNICO DE CLIENTES
 * Un cliente puede ser: SALA_CORTE | DISTRIBUCION | AMBOS
 * Todos los módulos referencian el mismo cliente mediante cliente_id.
 *
 * COLUMNAS REALES de public.clientes (actualizado con nuevos campos):
 *   id, nombre, nombre_comercial, nif_cif, telefono, email, contacto_principal,
 *   contacto, categoria, direccion, poblacion, provincia, codigo_postal,
 *   cliente_sala_corte, cliente_distribucion,
 *   tarifa_sin_emplatado_menor, tarifa_sin_emplatado_mayor, tarifa_emplatado,
 *   activo, created_at
 */

// ─── CONVERTERS ───────────────────────────────────────────────────────────────

const toClient = (db) => {
  if (!db) return null;
  return {
    id: db?.id,
    nombre: db?.nombre,
    nombreComercial: db?.nombre_comercial,
    nifCif: db?.nif_cif,
    telefono: db?.telefono,
    email: db?.email,
    contactoPrincipal: db?.contacto_principal,
    contacto: db?.contacto,
    categoria: db?.categoria,
    direccion: db?.direccion,
    poblacion: db?.poblacion,
    provincia: db?.provincia,
    codigoPostal: db?.codigo_postal,
    clienteSalaCorte: db?.cliente_sala_corte || false,
    clienteDistribucion: db?.cliente_distribucion || false,
    activo: db?.activo,
    createdAt: db?.created_at,

    // Tarifa fields (real columns)
    tarifaSinEmplatadoMenor: db?.tarifa_sin_emplatado_menor,
    tarifaSinEmplatadoMayor: db?.tarifa_sin_emplatado_mayor,
    tarifaEmplatado: db?.tarifa_emplatado,
  };
};

/**
 * toDb — maps ALL fields that actually exist in public.clientes.
 */
const toDb = (client) => {
  const payload = {};

  if (client?.nombre !== undefined) payload.nombre = client?.nombre;
  if (client?.nombreComercial !== undefined) payload.nombre_comercial = client?.nombreComercial || null;
  if (client?.nifCif !== undefined) payload.nif_cif = client?.nifCif || null;
  if (client?.telefono !== undefined) payload.telefono = client?.telefono || null;
  if (client?.email !== undefined) payload.email = client?.email || null;
  if (client?.contactoPrincipal !== undefined) payload.contacto_principal = client?.contactoPrincipal || null;
  if (client?.contacto !== undefined) payload.contacto = client?.contacto || null;
  if (client?.categoria !== undefined) payload.categoria = client?.categoria || null;
  if (client?.direccion !== undefined) payload.direccion = client?.direccion || null;
  if (client?.poblacion !== undefined) payload.poblacion = client?.poblacion || null;
  if (client?.provincia !== undefined) payload.provincia = client?.provincia || null;
  if (client?.codigoPostal !== undefined) payload.codigo_postal = client?.codigoPostal || null;
  if (client?.clienteSalaCorte !== undefined) payload.cliente_sala_corte = !!client?.clienteSalaCorte;
  if (client?.clienteDistribucion !== undefined) payload.cliente_distribucion = !!client?.clienteDistribucion;
  if (client?.activo !== undefined) payload.activo = client?.activo;
  if (client?.tarifaSinEmplatadoMenor !== undefined) payload.tarifa_sin_emplatado_menor = client?.tarifaSinEmplatadoMenor || null;
  if (client?.tarifaSinEmplatadoMayor !== undefined) payload.tarifa_sin_emplatado_mayor = client?.tarifaSinEmplatadoMayor || null;
  if (client?.tarifaEmplatado !== undefined) payload.tarifa_emplatado = client?.tarifaEmplatado || null;

  return payload;
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

export const getClientType = (client) => {
  if (client?.clienteSalaCorte && client?.clienteDistribucion) return 'AMBOS';
  if (client?.clienteSalaCorte) return 'SALA_CORTE';
  if (client?.clienteDistribucion) return 'DISTRIBUCION';
  return 'NINGUNO';
};

export const CLIENT_TYPE_LABELS = {
  SALA_CORTE: 'Sala de Corte',
  DISTRIBUCION: 'Distribución',
  AMBOS: 'Ambos',
  NINGUNO: 'Cliente',
};

export const CLIENT_TYPE_COLORS = {
  SALA_CORTE: '#C9A24D',
  DISTRIBUCION: '#8097D6',
  AMBOS: '#D6EF4E',
  NINGUNO: 'rgba(255,255,255,0.3)',
};

// ─── SERVICE ──────────────────────────────────────────────────────────────────

export const unifiedClientService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('clientes')
      ?.select('*')
      ?.order('nombre', { ascending: true });

    if (filters?.activo !== undefined) query = query?.eq('activo', filters?.activo);

    const { data, error } = await query;
    if (error) throw error;
    return (data || [])?.map(toClient);
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('clientes')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;
    return toClient(data);
  },

  async create(clientData) {
    const payload = toDb(clientData);
    if (payload?.activo === undefined) payload.activo = true;
    const { data, error } = await supabase
      ?.from('clientes')
      ?.insert(payload)
      ?.select()
      ?.single();
    if (error) throw error;
    return toClient(data);
  },

  async update(id, clientData) {
    const payload = toDb(clientData);
    const { data, error } = await supabase
      ?.from('clientes')
      ?.update(payload)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return toClient(data);
  },

  async toggleActive(id, newStatus) {
    const { data, error } = await supabase
      ?.from('clientes')
      ?.update({ activo: newStatus })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return toClient(data);
  },

  async delete(id) {
    const { error } = await supabase?.from('clientes')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  // Historial de loncheados del cliente — con joins a productos y empleados
  async getLoncheadosHistory(clienteId, limit = 100) {
    const { data, error } = await supabase
      ?.from('loncheados')
      ?.select(`
        id,
        created_at,
        fecha_produccion,
        peso_inicial,
        peso_aprovechado,
        merma,
        rendimiento,
        tipo,
        emplatado,
        observaciones,
        estado_rendimiento,
        plan_trabajo_id,
        producto:productos(id, nombre),
        empleado:empleados(id, nombre)
      `)
      ?.eq('cliente_id', clienteId)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);
    if (error) throw error;
    return data || [];
  },

  // Rentabilidad del cliente calculada desde loncheados + ldd_rentabilidad_control
  async getRentabilidadByCliente(clienteId, filters = {}) {
    try {
      let query = supabase
        ?.from('loncheados')
        ?.select(`
          id,
          created_at,
          fecha_produccion,
          peso_inicial,
          peso_aprovechado,
          merma,
          rendimiento,
          precio_cliente,
          tarifa,
          coste,
          coste_sala,
          tipo,
          emplatado,
          producto:productos(id, nombre),
          empleado:empleados(id, nombre),
          rentabilidad:ldd_rentabilidad_control(
            ingresos_totales,
            coste_total,
            coste_salario,
            coste_plasticos_total,
            beneficio,
            rentabilidad_pct
          )
        `)
        ?.eq('cliente_id', clienteId)
        ?.order('created_at', { ascending: false });

      if (filters?.fechaDesde) query = query?.gte('created_at', filters?.fechaDesde);
      if (filters?.fechaHasta) query = query?.lte('created_at', filters?.fechaHasta + 'T23:59:59');

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('[unifiedClientService.getRentabilidadByCliente]', err);
      return [];
    }
  },

  // Próximos trabajos del cliente desde plan_trabajo
  async getProximosTrabajos(clienteId, limit = 10) {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const { data, error } = await supabase
        ?.from('plan_trabajo')
        ?.select(`
          id, fecha, estado, cantidad_piezas, tipo_trabajo,
          producto:productos(id, nombre),
          empleado:empleados(id, nombre)
        `)
        ?.eq('cliente_id', clienteId)
        ?.gte('fecha', today)
        ?.not('estado', 'in', '("COMPLETADO","CANCELADO")')
        ?.order('fecha', { ascending: true })
        ?.limit(limit);
      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('[unifiedClientService.getProximosTrabajos]', err);
      return [];
    }
  },
};

export default unifiedClientService;
